// Helpers de gráfico de série hidrológica: escala do eixo Y, poda de buracos da
// grade e unidade/forma por grandeza.
//
// Portado de `monitoramento-frontend/src/escala.ts` (portal do cliente, em
// produção desde 2026-08-05) — as regras nasceram de leituras erradas na tela,
// com dados reais da CRVR. Documento de origem:
// `dre-sistema/docs/graficos-hidrologicos.md`.
//
// Módulo PURO de propósito (sem React, sem antd): serve o portal e o ERP.

export type DominioY = [number | 'auto', number | 'auto'];

// ── Eixo Y da série ──────────────────────────────────────────────────────────
// 'auto' nos dois extremos aproxima ao dado — ótimo pra ver variação real,
// péssimo com rio parado: o ruído de ±1 cm do sensor vira um serrote de tela
// cheia (Francisquinho parado em 1,25 m abriu o eixo de 1,25 a 1,26). Regras:
//   - barras (chuva): ancora no zero — barra nasce do chão;
//   - nível: AMPLITUDE MÍNIMA de 1,0 m centrada nos dados; quando a variação
//     real passa disso (cheia), volta ao auto pra mostrar a subida inteira;
//   - demais grandezas: auto puro.
export const SPAN_MINIMO_NIVEL = 1.0;

export function dominioY(
  valores: Array<number | null | undefined>,
  grandeza: string,
  emBarras: boolean,
): DominioY {
  if (emBarras) return [0, 'auto'];
  if (grandeza !== 'nivel') return ['auto', 'auto'];
  let lo = Infinity, hi = -Infinity;
  for (const v of valores) {
    if (v == null || !Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo) || hi - lo >= SPAN_MINIMO_NIVEL) return ['auto', 'auto'];
  let base = (lo + hi - SPAN_MINIMO_NIVEL) / 2;
  if (base < 0 && lo >= 0) base = 0;          // dados positivos não afundam o eixo
  const piso = Math.floor(base * 10) / 10;    // arredonda pra ticks limpos (0,1 m)
  return [piso, Math.ceil((piso + SPAN_MINIMO_NIVEL) * 10) / 10];
}

// ── Eixo do hidrograma / vazão ───────────────────────────────────────────────
// Calculado à mão porque o 'auto' do recharts, com um <Scatter data={...}>
// dentro do ComposedChart, dimensiona o eixo só por ESSA série — as medições
// (máx. ~3 m³/s) decapitavam os picos do hidrograma (~8 m³/s). Considera série
// E medições; começa em 0 por convenção de hidrograma (o clamp h≤h0→0 garante
// que não há vazão negativa).
export function dominioVazao(valores: Array<number | null | undefined>): DominioY {
  let hi = -Infinity;
  for (const v of valores) {
    if (v != null && Number.isFinite(v) && v > hi) hi = v;
  }
  if (!Number.isFinite(hi) || hi <= 0) return [0, 'auto'];
  return [0, Math.ceil(hi * 1.05 * 10) / 10];   // 5% de folga, teto em 0,1
}

// Domínio X explícito: união da série contínua com os pontos avulsos. Sem isso,
// o Scatter com `data` própria encolhe o eixo X para o intervalo entre a 1ª e a
// última MEDIÇÃO — "Tudo" sumia com metade da série.
export function dominioX(
  serie: Array<{ t: number }>,
  avulsos: Array<{ t: number }>,
): [number | 'dataMin', number | 'dataMax'] {
  let lo = Infinity, hi = -Infinity;
  for (const p of serie) { if (p.t < lo) lo = p.t; if (p.t > hi) hi = p.t; }
  for (const m of avulsos) { if (m.t < lo) lo = m.t; if (m.t > hi) hi = m.t; }
  return lo < hi ? [lo, hi] : ['dataMin', 'dataMax'];
}

// ── Poda de buracos da grade ─────────────────────────────────────────────────
// A série de telemetria vem BUCKETIZADA numa grade temporal com casas vazias
// onde não houve transmissão (é assim de propósito: revela falha de
// transmissão). Com a linha quebrando em toda casa vazia (connectNulls off), o
// padrão real do LogBox — registra a cada 1 min, envia em RAJADAS de ~10 min —
// vira linha picotada, e ponto isolado entre casas vazias fica invisível.
//
// Regra: remove as casas vazias e deixa a linha conectar pontos medidos
// vizinhos, MAS um buraco maior que `limiarMs` mantém UMA casa nula no meio —
// ali a linha quebra de propósito (uma parada de 30 h deve aparecer como
// interrupção, não como reta mentirosa atravessando o fim de semana).
export type PontoGrafico = { t: number; valor: number | null; consistido: number | null };

export function podarBuracos<P extends PontoGrafico>(pontos: P[], limiarMs: number): Array<P | PontoGrafico> {
  const reais = pontos.filter((p) => p.valor != null || p.consistido != null);
  const out: Array<P | PontoGrafico> = [];
  for (let i = 0; i < reais.length; i++) {
    if (i > 0 && reais[i].t - reais[i - 1].t > limiarMs) {
      out.push({ t: (reais[i - 1].t + reais[i].t) / 2, valor: null, consistido: null });
    }
    out.push(reais[i]);
  }
  return out;
}

// Buraco tolerado: 6 casas da grade, com piso de 1 h — cobre a rajada típica do
// modem (10 min numa grade de 1 min) sem esconder paradas de verdade.
export function limiarBuracoMs(resolucaoSeg: number | null | undefined): number {
  return Math.max(6 * (resolucaoSeg || 0) * 1000, 3600_000);
}

// ── Grandeza, unidade e forma ────────────────────────────────────────────────
// A `unidade` cadastrada na estação vale só pro NÍVEL. Chuva é mm, vazão é
// m³/s, tensão é V. Deduzir pelo nome da variável evita o clássico "NivelBat"
// (tensão da bateria) aparecendo como nível em metros.
export type Grandeza = 'nivel' | 'chuva' | 'vazao' | 'tensao' | 'outra';

export function grandezaDe(variavel: string): Grandeza {
  const v = String(variavel || '');
  // bateria ANTES de nível: "NivelBat" casa nos dois, e é tensão
  if (/bat|tens|volt/i.test(v)) return 'tensao';
  if (/mm|chuva|precip|rain|pluv/i.test(v)) return 'chuva';
  if (/vaz|flow|descarga/i.test(v)) return 'vazao';
  if (/nivel|nível|cota|level|stage/i.test(v)) return 'nivel';
  return 'outra';
}

export function unidadeDe(variavel: string, unidadeEstacao?: string | null): string {
  switch (grandezaDe(variavel)) {
    case 'nivel': return unidadeEstacao || 'm';
    case 'chuva': return /h\b|hora/i.test(variavel) ? 'mm/h' : 'mm';
    case 'vazao': return 'm³/s';
    case 'tensao': return 'V';
    default: return '';
  }
}

export function casasDe(variavel: string): number {
  switch (grandezaDe(variavel)) {
    case 'chuva': return 1;
    case 'nivel': return 2;
    case 'vazao': return 3;
    case 'tensao': return 2;
    default: return 2;
  }
}

// Chuva se lê como HIETOGRAMA: barra por leitura, ancorada no zero. Ligar
// pontos de chuva com linha é errado de leitura.
export function ehBarras(variavel: string): boolean {
  return grandezaDe(variavel) === 'chuva';
}

export function fmtValor(v: number | null | undefined, variavel: string, unidadeEstacao?: string | null): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const u = unidadeDe(variavel, unidadeEstacao);
  return `${Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: casasDe(variavel), maximumFractionDigits: casasDe(variavel),
  })}${u ? ' ' + u : ''}`;
}

// ── Janela ancorada na última leitura ────────────────────────────────────────
// Régua lida a cada 3–7 dias + "24h" terminando AGORA = gráfico vazio quase
// sempre; o cliente abre a estação e acha que o sistema perdeu os dados.
// "7d" passa a significar "os últimos 7 dias COM DADOS". Estação viva não muda
// nada (âncora ≈ agora).
export function janelaAncorada(
  dias: number,
  ancoraIso: string | null | undefined,
  agoraMs: number = Date.now(),
): { de: string | null; ate: string | null } {
  if (!dias) return { de: null, ate: null };          // "Tudo"
  const ancora = ancoraIso ? new Date(ancoraIso).getTime() : NaN;
  const fim = (!Number.isFinite(ancora) || ancora > agoraMs) ? agoraMs : ancora;
  return {
    de: new Date(fim - dias * 86400_000).toISOString(),
    ate: new Date(fim + 3600_000).toISOString(),      // 1 h de folga à frente
  };
}

// Âncora "velha" (>2 h no passado) precisa ser avisada, senão uma janela de
// semanas atrás parece dado atual.
export function ancoraDefasada(ancoraIso: string | null | undefined, agoraMs: number = Date.now()): boolean {
  if (!ancoraIso) return false;
  const a = new Date(ancoraIso).getTime();
  return Number.isFinite(a) && agoraMs - a > 2 * 3600_000;
}
